<!-- eslint-disable @typescript-eslint/naming-convention -->
<script setup lang="ts">
import { DomHandler } from 'primevue/utils';
import { computed, inject, nextTick, onMounted, ref } from 'vue';

import MenuInstance from 'lib/components/menu/Menu.vue.d';

import { TreeNodeProps, TreeSelectionKeys } from './TreeNode.vue.d';
import { TreeNode as TTreeNode } from '../basetree/BaseTree.vue.d';
import Button from '../button/Button.vue';
import Checkbox from '../checkbox/Checkbox.vue';
import Icon from '../icon/Icon.vue';
import { WangsIcons } from '../icon/Icon.vue.d';
import Menu from '../menu/Menu.vue';

const props = defineProps<TreeNodeProps>();

const emit = defineEmits<{
  nodeToggle: [node: TTreeNode];
  nodeClick: [
    data: { originalEvent: Event; nodeTouched: boolean; node: TTreeNode },
  ];
  checkboxChange: [
    data: {
      node: TTreeNode;
      check: boolean;
      selectionKeys: TreeSelectionKeys;
    },
  ];
  toggleMenu: [node: TTreeNode];
}>();

const Preset = inject<Record<string, any>>('preset', {}).tree;

const treeNodeMenu = ref<MenuInstance>();

const currentNode = ref<Element | null>(null);
let nodeTouched = false;
let toggleClicked = false;

// Computed properties
const hasChildren = computed(
  () => props.node?.children && props.node.children.length > 0,
);

const enableNodeAction = computed(
  () =>
    +props.node.key != -1 &&
    props.useOption &&
    !props.listLabelsNoOption?.includes(props.node.name),
);

const expanded = computed(
  () => props.expandedKeys && props.expandedKeys[props.node.key] === true,
);
const leaf = computed(() =>
  props.node.leaf === false
    ? false
    : !(props.node.children && props.node.children.length),
);

const selectable = computed(() => {
  if (isDisabled(props.node) || isExactDisabled(props.node)) return false;

  return props.node.selectable === false ? false : props.selectionMode != null;
});

const selected = computed(
  () =>
    props.nodeSelectedWhen?.(props.node) ||
    (props.selectionMode && props.selectionKeys
      ? props.selectionKeys[props.node.key] === true
      : false),
);

const checkboxMode = computed(() => props.selectionMode === 'checkbox');

const checked = computed(
  () =>
    props.nodeSelectedWhen?.(props.node) ||
    (props.selectionKeys
      ? props.selectionKeys[props.node.key]?.checked
      : false),
);
const partialChecked = computed(() =>
  props.selectionKeys
    ? props.selectionKeys[props.node.key]?.partialChecked
    : false,
);
const ariaChecked = computed(() =>
  props.selectionMode === 'single' || props.selectionMode === 'multiple'
    ? selected.value
    : undefined,
);
const ariaSelected = computed(() =>
  checkboxMode.value ? checked.value : undefined,
);

// Methods
const toggle = (): void => {
  emit('nodeToggle', props.node);
  toggleClicked = true;
};

const label = (node: TTreeNode): string => {
  return node.label;
};

const onChildNodeToggle = (node: TTreeNode): void => {
  emit('nodeToggle', node);
};

const onClick = (event: PointerEvent | KeyboardEvent): void => {
  if (
    toggleClicked ||
    DomHandler.getAttribute(
      event.target as HTMLElement,
      '[data-pc-section="toggler"]',
    ) ||
    DomHandler.getAttribute(
      (event.target as HTMLElement).parentElement,
      '[data-pc-section="toggler"]',
    )
  ) {
    toggleClicked = false;
    return;
  }

  if (isCheckboxSelectionMode()) {
    toggleCheckbox(event.type === 'dblclick' || props.node.key === -1);
  } else {
    emit('nodeClick', {
      originalEvent: event,
      nodeTouched,
      node: { ...props.node, selectable: selectable.value }, // To prevent selection when the node disabled by disableKeys or exactDisableKeys
    });
  }

  nodeTouched = false;
};

const onChildNodeClick = (event: any): void => {
  emit('nodeClick', event);
};

const onTouchEnd = (): void => {
  nodeTouched = true;
};

const setTabIndexForSelectionMode = (event, paramNodeTouched): void => {
  if (props.selectionMode !== null) {
    const elements = [
      ...DomHandler.find(currentNode.value.parentElement, '[role="treeitem"]'),
    ];

    event.currentTarget.tabIndex = paramNodeTouched === false ? -1 : 0;

    if (elements.every((element) => element.tabIndex === -1)) {
      elements[0].tabIndex = 0;
    }
  }
};

const focusRowChange = (
  firstFocusableRow: HTMLElement,
  currentFocusedRow: HTMLElement,
  lastVisibleDescendant?: any,
): void => {
  firstFocusableRow.tabIndex = -1;
  currentFocusedRow.tabIndex = 0;

  focusNode(lastVisibleDescendant || currentFocusedRow);
};

const findBeforeClickableNode = (node: HTMLElement): HTMLElement => {
  const parentListElement = node.closest('ul').closest('li');

  if (parentListElement) {
    const prevNodeButton = DomHandler.findSingle(parentListElement, 'button');

    if (prevNodeButton && prevNodeButton.style.visibility !== 'hidden') {
      return parentListElement;
    }

    return findBeforeClickableNode(node.previousElementSibling as HTMLElement);
  }

  return null;
};

const setAllNodesTabIndexes = (): void => {
  if (!currentNode.value) return;

  const nodes = DomHandler.find(
    currentNode.value.closest('[data-pc-section="container"]'),
    '[role="treeitem"]',
  );
  const hasSelectedNode = [...nodes].some(
    (node) =>
      node.getAttribute('aria-selected') === 'true' ||
      node.getAttribute('aria-checked') === 'true',
  );

  [...nodes].forEach((node) => {
    node.tabIndex = -1;
  });

  if (hasSelectedNode) {
    const selectedNodes = [...nodes].filter(
      (node) =>
        node.getAttribute('aria-selected') === 'true' ||
        node.getAttribute('aria-checked') === 'true',
    );
    selectedNodes[0].tabIndex = 0;
    return;
  }

  [...nodes][0].tabIndex = 0;
};

const toggleCheckbox = (propagateRecursively = false): void => {
  const _selectionKeys = props.selectionKeys ? { ...props.selectionKeys } : {};
  const _check = !checked.value;

  propagateDown(props.node, _check, _selectionKeys, propagateRecursively);

  emit('checkboxChange', {
    node: props.node,
    check: _check,
    selectionKeys: _selectionKeys,
  });
};

const propagateDown = (
  node: TTreeNode,
  check: boolean,
  selectionKeys: TreeSelectionKeys,
  propagateRecursively = false,
): void => {
  if (check) {
    selectionKeys[node.key] = { checked: true, partialChecked: false };
  } else {
    delete selectionKeys[node.key];
  }

  if (propagateRecursively || props.propagateSelection) {
    if (node.children && node.children.length) {
      for (const child of node.children) {
        propagateDown(child, check, selectionKeys, propagateRecursively);
      }
    }
  }
};

const propagateUp = (event: {
  check: boolean;
  selectionKeys: TreeSelectionKeys;
  node: TTreeNode;
}): void => {
  const { check } = event;
  const _selectionKeys = { ...event.selectionKeys };
  let checkedChildCount = 0;
  let childPartialSelected = false;

  if (!props.node.children) return;

  if (props.propagateSelection) {
    for (const child of props.node.children) {
      if (_selectionKeys[child.key]?.checked) checkedChildCount++;
      else if (_selectionKeys[child.key]?.partialChecked)
        childPartialSelected = true;
    }

    if (check && checkedChildCount === props.node.children.length) {
      _selectionKeys[props.node.key] = { checked: true, partialChecked: false };
    } else {
      if (!check) {
        delete _selectionKeys[props.node.key];
      }

      if (
        childPartialSelected ||
        (checkedChildCount > 0 &&
          checkedChildCount !== props.node.children.length)
      ) {
        _selectionKeys[props.node.key] = {
          checked: false,
          partialChecked: true,
        };
      } else {
        delete _selectionKeys[props.node.key];
      }
    }
  }

  emit('checkboxChange', {
    node: event.node,
    check: event.check,
    selectionKeys: _selectionKeys,
  });
};

const isCheckboxSelectionMode = (): boolean =>
  props.selectionMode === 'checkbox';

// Keyboard navigation methods
const onKeyDown = (event: KeyboardEvent): void => {
  if (!isSameNode(event)) return;

  switch (event.code) {
    case 'Tab':
      onTabKey();
      break;
    case 'ArrowDown':
      onArrowDown(event);
      break;
    case 'ArrowUp':
      onArrowUp(event);
      break;
    case 'ArrowRight':
      onArrowRight(event);
      break;
    case 'ArrowLeft':
      onArrowLeft(event);
      break;
    case 'Enter':
    case 'NumpadEnter':
    case 'Space':
      onEnterKey(event);
      break;
  }
};

const onArrowDown = (event: KeyboardEvent): void => {
  const nodeElement =
    (event.target as HTMLElement).getAttribute('data-wv-section') ===
    'tree-node-toggler'
      ? ((event.target as HTMLElement).closest(
          '[role="treeitem"]',
        ) as HTMLElement)
      : (event.target as HTMLElement);

  const listElement = nodeElement.children[1] as HTMLElement;

  if (listElement) {
    focusRowChange(nodeElement, listElement.children[0] as HTMLElement);
  } else {
    if (nodeElement.nextElementSibling) {
      focusRowChange(
        nodeElement,
        nodeElement.nextElementSibling as HTMLElement,
      );
    } else {
      const nextSiblingAncestor = findNextSiblingOfAncestor(
        nodeElement,
      ) as HTMLElement;

      if (nextSiblingAncestor) {
        focusRowChange(nodeElement, nextSiblingAncestor);
      }
    }
  }

  event.preventDefault();
};

const onArrowUp = (event: KeyboardEvent): void => {
  const nodeElement = event.target as HTMLElement;

  if (nodeElement.previousElementSibling) {
    focusRowChange(
      nodeElement,
      nodeElement.previousElementSibling as HTMLElement,
      findLastVisibleDescendant(
        nodeElement.previousElementSibling as HTMLElement,
      ),
    );
  } else {
    const parentNodeElement = getParentNodeElement(nodeElement) as HTMLElement;

    if (parentNodeElement) {
      focusRowChange(nodeElement, parentNodeElement);
    }
  }

  event.preventDefault();
};

const onArrowRight = (event: KeyboardEvent): void => {
  if (leaf.value || expanded.value) return;

  (event.currentTarget as HTMLElement).tabIndex = -1;

  emit('nodeToggle', props.node);

  nextTick(() => {
    onArrowDown(event);
  });
};

const onArrowLeft = (event: KeyboardEvent): boolean => {
  const togglerElement = DomHandler.findSingle(
    event.currentTarget as HTMLElement,
    '[data-wv-section="tree-node-toggler"]',
  );

  if (props.level === 0 && !expanded.value) {
    return false;
  }

  if (expanded.value && !leaf.value) {
    togglerElement.click();

    return false;
  }

  const target = findBeforeClickableNode(
    event.currentTarget as HTMLElement,
  ) as HTMLElement;

  if (target) {
    focusRowChange(event.currentTarget as HTMLElement, target);
  }
};

const onEnterKey = (event: KeyboardEvent): void => {
  setTabIndexForSelectionMode(event, nodeTouched);
  onClick(event);

  event.preventDefault();
};

const onTabKey = (): void => {
  setAllNodesTabIndexes();
};

const isSameNode = (event): boolean => {
  return (
    event.currentTarget &&
    (event.currentTarget.isSameNode(event.target) ||
      event.currentTarget.isSameNode(event.target.closest('[role="treeitem"]')))
  );
};

/**
 * Toggles the visibility of node menu intelligently.
 *
 * When the user clicks on a button menu, the menu becomes visible.
 * However, if the user clicks on another button while the menu is still visible, clicking the new button will only hide the menu instead of showing the new menu.
 *
 * This function fixes that problem by ensuring that clicking on a button while the menu is visible will only hide the menu and opens the new menu.
 *
 * @param {MouseEvent} event - The mouse event that triggered the toggle.
 * @param {TreeNode} node - The tree node associated with the menu.
 */
const toggleNodeMenu = async (
  event: MouseEvent,
  node: TTreeNode,
): Promise<void> => {
  const target = event.target as HTMLElement;
  const button =
    target.tagName.toLowerCase() === 'button' ? target : target.parentElement;

  if (button?.classList.contains('tree-menu-button-active')) {
    /**
     * If the clicked button is already active, remove the active class from all buttons to hide the menu.
     */
    removeClassActive();
    const toggleEvent: Event = {
      ...event,
      currentTarget: event.target,
    };
    treeNodeMenu.value?.toggle(toggleEvent);
  } else {
    /**
     * If the clicked button is not active, wait for the menu to be successfully hidden,
     * remove the active class from all buttons, add the active class to the clicked button,
     * show the menu, and emit a `toggleMenu` event with the clicked node.
     */
    await nextTick(); // Wait for the menu to be successfully hidden.
    removeClassActive();
    button?.classList.add('tree-menu-button-active', '!bg-surface-300/20');

    const toggleEvent: Event = {
      ...event,
      currentTarget: event.target,
    };

    treeNodeMenu.value?.toggle(toggleEvent);
    emit('toggleMenu', node);
  }
};

/**
 * Removes the active class from all buttons that have it.
 *
 * @returns {void}
 */
const removeClassActive = (): void => {
  const previouslyClicked = document.querySelectorAll(
    '.tree-menu-button-active',
  );

  previouslyClicked?.forEach((el) => {
    el.classList.remove('tree-menu-button-active', '!bg-surface-300/20');
  });
};

const findNextSiblingOfAncestor = (nodeElement: Element): Element => {
  const parentNodeElement = getParentNodeElement(nodeElement);

  if (parentNodeElement) {
    if (parentNodeElement.nextElementSibling)
      return parentNodeElement.nextElementSibling;
    return findNextSiblingOfAncestor(parentNodeElement);
  }
  return null;
};

const findLastVisibleDescendant = (nodeElement: Element): Element => {
  const childrenListElement = nodeElement.children[1];

  if (childrenListElement) {
    const lastChildElement =
      childrenListElement.children[childrenListElement.children.length - 1];

    return findLastVisibleDescendant(lastChildElement as HTMLElement);
  }
  return nodeElement;
};

const getParentNodeElement = (nodeElement: Element): Element => {
  const parentNodeElement = nodeElement.parentElement.parentElement;

  return DomHandler.getAttribute(parentNodeElement, 'role') === 'treeitem'
    ? parentNodeElement
    : null;
};

const focusNode = (element: HTMLElement): void => {
  element.focus();
};

const isDisabled = (node: typeof props.node): boolean => {
  return (
    props.disableKeys?.includes(node.key) ||
    props.disabled ||
    node.selectable == false ||
    node.disabled == true ||
    props.disableNodeWhen?.(node)
  );
};

const isExactDisabled = (node: typeof props.node): boolean => {
  return (
    node.selectable == false ||
    props.exactDisableKeys?.includes(node.key) ||
    (props.disableNodeAll && +node.key === -1) ||
    props.exactDisableNodeWhen?.(node)
  );
};

// ... Rest of keyboard navigation methods remain the same but converted to arrow functions ...

onMounted(() => {
  setAllNodesTabIndexes();
});
</script>

<template>
  <li
    ref="currentNode"
    :aria-checked="ariaChecked"
    :aria-expanded="expanded"
    :aria-label="label(node)"
    :aria-level="level"
    :aria-posinset="index + 1"
    :aria-selected="ariaSelected"
    :aria-setsize="node.children ? node.children.length : 0"
    :tabindex="index === 0 ? 0 : -1"
    @keydown="onKeyDown"
    v-bind="(props.pt ?? Preset)?.node && $attrs"
    data-wv-name="treenode"
    role="treeitem"
  >
    <div
      v-bind="
        (props.pt ?? Preset)?.content({
          props,
          context: {
            selected: checked || selected,
            disabled: isDisabled(node),
            exactDisabled: isExactDisabled(node),
          },
        })
      "
      :data-p-highlight="checkboxMode ? checked : selected"
      :data-p-selectable="selectable"
      :style="node.style"
      @click="onClick"
      @touchend="onTouchEnd"
    >
      <button
        @click="toggle"
        aria-hidden="true"
        tabindex="-1"
        type="button"
        v-bind="(props.pt ?? Preset)?.toggler({ context: { leaf } })"
      >
        <template v-if="node.loading && loadingMode === 'icon'">
          <Icon class="animate-spin" icon="spinner" />
        </template>

        <template v-else>
          <component
            :is="templates['togglericon']"
            v-if="templates['togglericon']"
            :class="[...(props.pt ?? Preset)?.togglericon.class]"
            :expanded="expanded"
            :node="node"
          />

          <Icon
            v-else
            :class="[
              ...(props.pt ?? Preset)?.togglericon.class,
              { 'rotate-90': expanded },
            ]"
            data-wv-section="togglericon"
            icon="arrow-right"
          />
        </template>
      </button>

      <Checkbox
        v-if="checkboxMode"
        :binary="false"
        v-bind="(props.pt ?? Preset)?.nodeCheckbox.root"
        :disabled="isDisabled(node) || isExactDisabled(node)"
        :model-value="checked ? checked : partialChecked ? false : null"
        @click.prevent=""
        @keydown.prevent.stop=""
        @update:model-value="onClick"
        data-wv-section="nodecheckbox"
        tri-state
      />

      <component
        :is="templates['nodeicon']"
        v-if="templates['nodeicon']"
        :class="[...(props.pt ?? Preset)?.nodeicon?.()?.class]"
        :node="node"
      />

      <span
        v-else
        :class="[...(props.pt ?? Preset)?.nodeicon?.()?.class, node.icon]"
      />

      <span
        v-bind="
          (props.pt ?? Preset)?.label({
            props,
            context: {
              selected: checked || selected,
              disabled: isDisabled(node),
              exactDisabled: isExactDisabled(node),
            },
          })
        "
        @keydown.stop
      >
        <component
          :is="templates[node.type] || templates['default']"
          v-if="templates[node.type] || templates['default']"
          :checked="checked"
          :disabled="isExactDisabled(node) || isDisabled(node)"
          :node="node"
        />

        <span
          v-else
          :data-node-has-children="!!node.children?.length"
          :data-node-key="node.key"
          :data-node-selected="checked"
          class="grow"
        >
          {{ node.name }}
        </span>

        <Icon
          v-if="node.isDisposable && showDisposableGroups"
          class="!text-general-200 w-4 h-4 p-1"
          icon="disposal"
          info="Disposable Group"
          tooltip-pos="left"
        />

        <Button
          v-if="enableNodeAction"
          :class="[
            '!w-4 !h-4 !min-w-4 p-0.5',
            '!text-general-200',
            'rounded-full',
          ]"
          @click.stop="toggleNodeMenu($event, node)"
          data-wv-section="nodemenubutton"
          icon="more"
          severity="secondary"
          text
          tooltip="Action"
          tooltip-pos="left"
        />
      </span>
    </div>

    <ul
      v-if="hasChildren && expanded"
      role="group"
      v-bind="(props.pt ?? Preset)?.subgroup"
    >
      <TreeNode
        :key="childNode.key"
        v-for="childNode of node.children"
        v-bind="props"
        :class="[...(props.pt ?? Preset)?.node?.class]"
        :disable-keys="disableKeys"
        :disabled="isDisabled(childNode)"
        :exact-disable-keys="exactDisableKeys"
        :expanded-keys="expandedKeys"
        :level="level + 1"
        :list-labels-no-option="listLabelsNoOption"
        :loading-mode="loadingMode"
        :node="childNode"
        :propagate-selection="props.propagateSelection"
        :pt="pt"
        :selection-keys="selectionKeys"
        :selection-mode="selectionMode"
        :show-disposable-groups="showDisposableGroups"
        :templates="templates"
        :tree-node-menus="treeNodeMenus"
        :unstyled="unstyled"
        :use-option="useOption"
        @checkbox-change="propagateUp"
        @node-click="onChildNodeClick"
        @node-toggle="onChildNodeToggle"
        @toggle-menu="emit('toggleMenu', $event)"
      />
    </ul>
  </li>

  <Menu
    ref="treeNodeMenu"
    v-if="enableNodeAction"
    :model="treeNodeMenus"
    @hide="removeClassActive"
    data-wv-section="tree-node-menus"
    popup
  >
    <template #item="{ item }">
      <a
        aria-hidden="true"
        class="flex items-center justify-start gap-2 px-4 py-1.5 hover:bg-surface-50 text-xs no-underline overflow-hidden cursor-pointer select-none"
        data-pc-section="action"
        tabindex="-1"
      >
        <Icon
          :icon="item.icon as WangsIcons"
          :severity="item.danger ? 'danger' : undefined"
        />

        <span
          :class="['text-inherit', { '!text-danger-500': item.danger }]"
          data-pc-section="label"
        >
          {{ item.label }}
        </span>
      </a>
    </template>
  </Menu>
</template>
